import { Formik, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useEffect, useState, useCallback, useRef } from 'react';
import { requireArenaSlug } from '@/utils/arena';
import { arenaService, SportCategory, UpdateArenaPayload } from '@/services/arenaService';
import AsyncSelect from 'react-select/async';
import { wilayahService, KotaOption } from '@/services/wilayahService';
import { Icon } from '@iconify/react';

// Types
export interface ArenaProfileValues {
  namaArena: string;
  kodeWilayah: string;
  kategoriArena: string[];
  kotaKabupaten: string;
  citySlug?: string;
  kecamatan: string;
  alamatLengkap: string;
  linkMaps: string;
  instagram: string;
  whatsapp: string;
  tiktok: string;
  youtube: string;
  description?: string;
}

// Initial Values
const arenaProfileInitial: ArenaProfileValues = {
  namaArena: '',
  kodeWilayah: '',
  kategoriArena: [],
  kotaKabupaten: '',
  citySlug: '',
  kecamatan: '',
  alamatLengkap: '',
  linkMaps: '',
  instagram: '',
  whatsapp: '',
  tiktok: '',
  youtube: '',
  description: '',
};

// Validation Schema
const arenaProfileSchema = Yup.object({
  namaArena: Yup.string().required('Nama arena wajib diisi'),
  kecamatan: Yup.string().required('Kecamatan wajib diisi'),
  kategoriArena: Yup.array().of(Yup.string()).min(1, 'Pilih minimal satu kategori arena'),
  kotaKabupaten: Yup.string().required('Kota/Kabupaten wajib diisi'),
  alamatLengkap: Yup.string().required('Alamat lengkap wajib diisi'),
  linkMaps: Yup.string().url('Format URL tidak valid'),
});

interface DataArenaTabProps {
  onSubmit: (values: ArenaProfileValues, tabId: string) => Promise<void>;
}

const DataArenaTab = ({ onSubmit }: DataArenaTabProps) => {
  const [kategoriOptions, setKategoriOptions] = useState<
    { value: string; label: string; icon: string }[]
  >([]);
  const [initialValues, setInitialValues] = useState<ArenaProfileValues>(arenaProfileInitial);
  const [selectedKota, setSelectedKota] = useState<KotaOption | null>(null);
  const mountedRef = useRef(true);

  const fetchArena = useCallback(async () => {
    try {
      const slug = requireArenaSlug();
      const arena = await arenaService.getArenaBySlug(slug);
      if (!mountedRef.current || !arena) return;

      const values: ArenaProfileValues = {
        namaArena: arena.name || '',
        kodeWilayah: arena.kode_wilayah || '',
        kategoriArena: arena.sport_categories?.categories || [],
        kotaKabupaten: (arena.maps && arena.maps.city) || '',
        citySlug: (arena.maps && (arena.maps as any).city_slug) || '',
        kecamatan: (arena.maps && arena.maps.kecamatan) || '',
        alamatLengkap: (arena.maps && arena.maps.address) || '',
        linkMaps: (arena.maps && arena.maps.google_maps_url) || '',
        instagram: arena.social_links?.instagram || '',
        whatsapp: arena.social_links?.whatsapp || '',
        tiktok: arena.social_links?.tiktok || '',
        youtube: arena.social_links?.youtube || '',
        description: arena?.description || '',
      };

      setInitialValues(values);

      try {
        const citySlug = (arena.maps && (arena.maps as any).city_slug) || '';
        const cityName = (arena.maps && (arena.maps as any).city) || '';
        if (cityName) {
          const options = await wilayahService.searchKota(cityName);
          const kotaOpt = options.find((o) => o.value === citySlug) || options[0] || null;
          if (kotaOpt) setSelectedKota(kotaOpt);
        }
      } catch (_e) {
        // ignore
      }
    } catch (_err) {
      // ignore
    }
  }, []);

  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const searchKota = useCallback(
    (inputValue: string, callback: (options: KotaOption[]) => void) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      if (inputValue.length < 3) {
        callback([]);
        return;
      }

      debounceRef.current = setTimeout(async () => {
        const options = await wilayahService.searchKota(inputValue);
        callback(options);
      }, 500);
    },
    []
  );

  const kotaSelectStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      borderRadius: '0.75rem',
      borderColor: state.isFocused ? '#FC480D' : '#E5E7EB',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(252, 72, 13, 0.15)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#FC480D' : '#D1D5DB',
      },
      minHeight: '44px',
      fontSize: '0.75rem',
      backgroundColor: '#FFFFFF',
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#FC480D' : state.isFocused ? '#FFF5EE' : 'white',
      color: state.isSelected ? 'white' : '#0E1726',
      fontSize: '0.75rem',
      cursor: 'pointer',
    }),
    menu: (provided: any) => ({
      ...provided,
      borderRadius: '0.75rem',
      boxShadow: '0 4px 16px rgba(14,23,38,0.1)',
      zIndex: 50,
      border: '1px solid #E5E7EB',
      overflow: 'hidden',
    }),
  };

  // Load categories on mount
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      const cats: SportCategory[] = await arenaService.getSportCategories();
      if (!mounted) return;
      const opts = cats.map((c) => ({
        value: c.slug || String(c.id) || c.name.toLowerCase(),
        label: c.name,
        icon: c.icon || 'mdi:run-fast',
      }));
      setKategoriOptions(opts);
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  // Load arena details to prefill form
  useEffect(() => {
    mountedRef.current = true;
    fetchArena();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchArena]);

  const handleSubmit = async (values: ArenaProfileValues) => {
    try {
      const slug = requireArenaSlug();

      const payload: UpdateArenaPayload = {
        name: values.namaArena,
        kode_wilayah: values.kodeWilayah || '00000',
        kecamatan: values.kecamatan,
        sport_categories: values.kategoriArena.length > 0 ? values.kategoriArena : [],
        address: values.alamatLengkap,
        city: values.kotaKabupaten,
        city_slug: values.citySlug || undefined,
        google_maps_url: values.linkMaps,
        description: values.description || '',
        social_links: {
          instagram: values.instagram || undefined,
          whatsapp: values.whatsapp || undefined,
          tiktok: values.tiktok || undefined,
          youtube: values.youtube || undefined,
        },
      };

      const res = await arenaService.updateArena(slug, payload);
      if (res.success) {
        await fetchArena();
        await onSubmit(values, 'data-arena');
      } else {
        throw new Error(res.message || 'Failed to update arena');
      }
    } catch (err: any) {
      console.error('Update arena failed', err);
      throw err;
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={arenaProfileSchema}
      onSubmit={async (values, helpers) => {
        try {
          await handleSubmit(values);
        } catch (_err: any) {
          helpers.setSubmitting(false);
        }
      }}
    >
      {({ values, errors, touched, handleChange, setFieldValue, isSubmitting }) => (
        <Form id="active-profile-form" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            {/* Left Card: INFORMASI ARENA */}
            <div className="bg-white rounded-2xl border border-gray-200/80 p-6 shadow-2xs space-y-5">
              <div className="text-[11px] font-semibold text-gray-400 tracking-wider uppercase">
                INFORMASI ARENA
              </div>

              {/* Nama Arena */}
              <div>
                <label className="text-xs font-semibold text-[#0E1726] mb-1.5 block">
                  Nama arena
                </label>
                <input
                  type="text"
                  name="namaArena"
                  value={values.namaArena}
                  onChange={handleChange}
                  placeholder="Isikan nama arena"
                  className="w-full h-11 px-4 bg-white border border-gray-200 rounded-xl text-xs text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-colors"
                />
                {errors.namaArena && touched.namaArena && (
                  <p className="text-red-500 text-[11px] mt-1">{errors.namaArena}</p>
                )}
              </div>

              {/* Deskripsi */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-[#0E1726]">
                    Deskripsi (opsional)
                  </label>
                  <span className="text-[11px] text-gray-400 font-medium">
                    {values.description?.length || 0}/300
                  </span>
                </div>
                <textarea
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                  maxLength={300}
                  rows={4}
                  placeholder="Arena futsal dan badminton indoor dengan 4 lapangan, parkir luas, dan kantin."
                  className="w-full p-3.5 bg-white border border-gray-200 rounded-xl text-xs text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-colors resize-none h-24"
                />
              </div>

              {/* Logo Arena */}
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 flex flex-col items-center justify-center text-gray-400 gap-1 hover:border-orange-300 hover:bg-orange-50/20 transition-all cursor-pointer shrink-0">
                  <Icon icon="ph:image-bold" className="w-5 h-5 text-gray-400" />
                  <span className="text-[11px] font-medium text-gray-400">Logo</span>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-[#0E1726]">Logo arena</h4>
                  <p className="text-[11px] text-gray-400 leading-relaxed mt-1">
                    Rasio 1:1, minimal 400 × 400 px. Tampil di aplikasi pelanggan dan struk.
                  </p>
                </div>
              </div>

              {/* Galeri Arena */}
              <div>
                <label className="text-xs font-semibold text-[#0E1726] mb-2 block">
                  Galeri arena
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {['Galeri 1', 'Galeri 2', 'Galeri 3'].map((item) => (
                    <div
                      key={item}
                      className="aspect-[16/9] rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 flex flex-col items-center justify-center text-gray-400 gap-1 hover:border-orange-300 hover:bg-orange-50/20 transition-all cursor-pointer"
                    >
                      <Icon icon="ph:image-bold" className="w-5 h-5 text-gray-400" />
                      <span className="text-[11px] font-medium text-gray-400">{item}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-gray-400 leading-relaxed mt-2">
                  Maksimal 3 foto, rasio 16:9. Tampil di halaman arena pada aplikasi pelanggan.
                </p>
              </div>

              {/* Kategori Arena (Preserved feature) */}
              <div className="pt-3 border-t border-gray-100">
                <label className="text-xs font-semibold text-[#0E1726] mb-2.5 block">
                  Kategori Olahraga <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {kategoriOptions.map((kategori) => {
                    const isChecked = values.kategoriArena.includes(kategori.value);
                    return (
                      <button
                        type="button"
                        key={kategori.value}
                        onClick={() => {
                          if (isChecked) {
                            setFieldValue(
                              'kategoriArena',
                              values.kategoriArena.filter((k) => k !== kategori.value)
                            );
                          } else {
                            setFieldValue('kategoriArena', [...values.kategoriArena, kategori.value]);
                          }
                        }}
                        className={`h-9 px-3.5 rounded-full text-xs font-medium flex items-center gap-1.5 transition-all cursor-pointer border ${
                          isChecked
                            ? 'bg-[#FFF5EE] text-[#FC480D] border-orange-200/80 font-semibold shadow-2xs'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Icon icon={`mdi:${kategori.icon}`} className="w-4 h-4" />
                        <span>{kategori.label}</span>
                      </button>
                    );
                  })}
                </div>
                {errors.kategoriArena && (
                  <p className="text-red-500 text-[11px] mt-1">{errors.kategoriArena as string}</p>
                )}
              </div>
            </div>

            {/* Right Card: LOKASI */}
            <div className="bg-white rounded-2xl border border-gray-200/80 p-6 shadow-2xs space-y-5">
              <div className="text-[11px] font-semibold text-gray-400 tracking-wider uppercase">
                LOKASI
              </div>

              {/* Alamat Lengkap */}
              <div>
                <label className="text-xs font-semibold text-[#0E1726] mb-1.5 block">
                  Alamat lengkap
                </label>
                <textarea
                  name="alamatLengkap"
                  value={values.alamatLengkap}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Jl. Asia Afrika No. 8, Gelora"
                  className="w-full p-3.5 bg-white border border-gray-200 rounded-xl text-xs text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-colors resize-none h-20"
                />
                {errors.alamatLengkap && touched.alamatLengkap && (
                  <p className="text-red-500 text-[11px] mt-1">{errors.alamatLengkap}</p>
                )}
              </div>

              {/* Kecamatan & Kota/Kabupaten */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-[#0E1726] mb-1.5 block">
                    Kecamatan
                  </label>
                  <input
                    type="text"
                    name="kecamatan"
                    value={values.kecamatan}
                    onChange={handleChange}
                    placeholder="Tanah Abang"
                    className="w-full h-11 px-4 bg-white border border-gray-200 rounded-xl text-xs text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-colors"
                  />
                  {errors.kecamatan && touched.kecamatan && (
                    <p className="text-red-500 text-[11px] mt-1">{errors.kecamatan}</p>
                  )}
                </div>

                <div>
                  <label className="text-xs font-semibold text-[#0E1726] mb-1.5 block">
                    Kota / kabupaten
                  </label>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions={selectedKota ? [selectedKota] : []}
                    loadOptions={searchKota}
                    value={selectedKota}
                    onChange={(option: any) => {
                      if (option) {
                        setSelectedKota(option);
                        setFieldValue('kotaKabupaten', option?.nama || '');
                        setFieldValue('citySlug', option?.value || '');
                        setFieldValue('kodeWilayah', option?.value || '');
                      } else {
                        setSelectedKota(null);
                        setFieldValue('kotaKabupaten', '');
                        setFieldValue('citySlug', '');
                        setFieldValue('kodeWilayah', '');
                      }
                    }}
                    placeholder="Ketik minimal 3 huruf..."
                    noOptionsMessage={({ inputValue }: any) =>
                      inputValue && inputValue.length < 3
                        ? 'Ketik minimal 3 huruf'
                        : 'Tidak ada hasil ditemukan'
                    }
                    loadingMessage={() => 'Mencari...'}
                    isClearable
                    styles={kotaSelectStyles}
                    getOptionLabel={(option: any) => option.namaLengkap}
                    getOptionValue={(option: any) => option.value}
                  />
                  <ErrorMessage
                    name="kotaKabupaten"
                    component="div"
                    className="text-red-500 text-[11px] mt-1"
                  />
                </div>
              </div>

              {/* Link Google Maps */}
              <div>
                <label className="text-xs font-semibold text-[#0E1726] mb-1.5 block">
                  Link Google Maps
                </label>
                <input
                  type="text"
                  name="linkMaps"
                  value={values.linkMaps}
                  onChange={handleChange}
                  placeholder="https://maps.app.goo.gl/geloraSenayan"
                  className="w-full h-11 px-4 bg-white border border-gray-200 rounded-xl text-xs text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-colors"
                />
                {errors.linkMaps && touched.linkMaps && (
                  <p className="text-red-500 text-[11px] mt-1">{errors.linkMaps}</p>
                )}
              </div>
            </div>
          </div>

          {/* Bottom Save Action */}
          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-[#FC480D] hover:bg-[#E8490F] text-white text-xs font-semibold px-6 py-2.5 rounded-full transition-all cursor-pointer shadow-2xs active:scale-[0.98] disabled:opacity-50"
            >
              {isSubmitting ? 'Menyimpan...' : 'Simpan Perubahan'}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default DataArenaTab;
